import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Sparkles,
  Globe,
  Building2,
  Target,
  Link2,
  PartyPopper,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2
} from 'lucide-react';
import onboardingService, { OnboardingStatus, BusinessProfile } from '../../services/onboardingService';
import StepIndicator from '../../components/onboarding/StepIndicator';
import WelcomeStep from '../../components/onboarding/WelcomeStep';
import LanguageSelector from '../../components/layout/LanguageSelector';
import WebsiteStep from '../../components/onboarding/WebsiteStep';
import BriefingStep from '../../components/onboarding/BriefingStep';
import GoalsStep from '../../components/onboarding/GoalsStep';
import ConnectAccountsStep from '../../components/onboarding/ConnectAccountsStep';
import CompletionStep from '../../components/onboarding/CompletionStep';

const STEPS = [
  { id: 0, name: 'welcome', icon: Sparkles, titleKey: 'onboarding.steps.welcome' },
  { id: 1, name: 'website', icon: Globe, titleKey: 'onboarding.steps.website' },
  { id: 2, name: 'briefing', icon: Building2, titleKey: 'onboarding.steps.business' },
  { id: 3, name: 'goals', icon: Target, titleKey: 'onboarding.steps.goals' },
  { id: 4, name: 'connect', icon: Link2, titleKey: 'onboarding.steps.connect' },
  { id: 5, name: 'complete', icon: PartyPopper, titleKey: 'onboarding.steps.complete' }
];

const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [currentStep, setCurrentStep] = useState(0);
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load initial status
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const data = await onboardingService.getStatus();
        setStatus(data);
        setProfile(data.profile || null);

        // Handle URL parameters
        const stepParam = searchParams.get('step');
        const connectedPlatform = searchParams.get('connected');
        const errorParam = searchParams.get('error');
        const isNewProject = searchParams.get('new') === 'true';

        if (errorParam) {
          toast.error(decodeURIComponent(errorParam));
        }

        if (connectedPlatform) {
          toast.success(t('onboarding.platformConnected', { platform: connectedPlatform }));
        }

        // Set initial step
        if (stepParam) {
          setCurrentStep(parseInt(stepParam, 10));
        } else if (isNewProject) {
          // Force start from beginning for new projects
          setCurrentStep(0);
        } else if (data.isCompleted) {
          navigate('/dashboard');
        } else if (data.currentStep > 0) {
          setCurrentStep(data.currentStep);
        }
      } catch (error) {
        console.error('Error loading onboarding status:', error);
        toast.error(t('onboarding.loadError', 'Error loading data'));
      } finally {
        setIsLoading(false);
      }
    };

    loadStatus();
  }, [searchParams, navigate]);

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setIsSaving(true);
      try {
        await onboardingService.updateStep(currentStep + 1);
        const nextStep = currentStep + 1;

        // Refresh status before showing completion step to ensure we have latest data
        if (nextStep === 5) {
          await refreshStatus();
        }

        setCurrentStep(nextStep);
      } catch (error) {
        console.error('Error updating step:', error);
        toast.error(t('onboarding.nextError', 'Error advancing. Please try again.'));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleBack = async () => {
    if (currentStep > 0) {
      // Refresh status to get latest data before going back
      await refreshStatus();
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    await handleNext();
  };

  const updateProfile = (updates: Partial<BusinessProfile>) => {
    setProfile(prev => prev ? { ...prev, ...updates } : null);
  };

  const refreshStatus = async () => {
    try {
      const data = await onboardingService.getStatus();
      setStatus(data);
      setProfile(data.profile || null);
    } catch (error) {
      console.error('Error refreshing status:', error);
    }
  };

  const handleComplete = async () => {
    setIsSaving(true);
    try {
      await onboardingService.completeOnboarding();
      toast.success(t('onboarding.completeSuccess', 'Onboarding complete! Welcome to DevSkin Marketing!'));
      // Full page reload to guarantee fresh state (projects, auth, onboarding flags)
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error(t('onboarding.completeError', 'Error completing. Please try again.'));
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} />;
      case 1:
        return (
          <WebsiteStep
            profile={profile}
            updateProfile={updateProfile}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <BriefingStep
            profile={profile}
            updateProfile={updateProfile}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <GoalsStep
            profile={profile}
            updateProfile={updateProfile}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <ConnectAccountsStep
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 5:
        return (
          <CompletionStep
            profile={profile}
            status={status}
            onComplete={handleComplete}
            onBack={handleBack}
            isLoading={isSaving}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
              DevSkin Marketing
            </span>
          </div>

          <div className="flex items-center gap-4">
            {currentStep > 0 && currentStep < 5 && (
              <button
                onClick={handleSkip}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                {t('onboarding.skipStep', 'Skip this step')}
              </button>
            )}
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Step Indicator */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <StepIndicator steps={STEPS} currentStep={currentStep} />
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          {renderStep()}
        </div>
      </main>
    </div>
  );
};

export default OnboardingWizard;
